import * as i0 from '@angular/core';
import { EventEmitter, Output, Input, Directive, Pipe, NgModule, Injectable } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import * as i1$1 from '@angular/material/snack-bar';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import * as i1 from '@ngx-translate/core';
import { TranslateModule } from '@ngx-translate/core';
import { Subject, fromEvent, merge, timer, of, firstValueFrom, scheduled, asapScheduler, shareReplay } from 'rxjs';
import { takeUntil, tap, filter, map, switchMap } from 'rxjs/operators';
import { getSelectTypeFields, fieldType, flatSearchToRecursive, recursiveSearchToFlat } from '@azavista/advanced-search';
import { createPatch } from 'rfc6902';
let LongClickDirective = /*#__PURE__*/(() => {
  class LongClickDirective {
    constructor(elementRef) {
      this.elementRef = elementRef;
      /**
       * Minimum long press threshold duration in miliseconds
       * @ default = `1000` ms
       */
      // eslint-disable-next-line @angular-eslint/no-input-rename
      this.threshold = 1000;
      this.longClick = new EventEmitter();
      /** Override the DOM click */
      // eslint-disable-next-line @angular-eslint/no-output-native
      this.click = new EventEmitter();
      this.lastEvent = null;
      this.onDestroy$ = new Subject();
      this.subscribe(elementRef);
      fromEvent(window, 'touchmove').pipe(takeUntil(this.onDestroy$)).subscribe(() => {
        // clear when there is scrolling
        // to prevent the scrolling get treated as click event
        this.lastEvent = null;
      });
    }
    ngOnChanges(changes) {
      if (changes['threshold']) {
        this.subscribe(this.elementRef);
      }
    }
    ngOnDestroy() {
      this.onDestroy$.next();
      this.onDestroy$.complete();
    }
    subscribe(elementRef) {
      this.unsubscribe();
      const mousedown = fromEvent(elementRef.nativeElement, 'mousedown').pipe(tap(event => {
        this.lastEvent = event;
      }), filter(event => event.button == 0), map(event => true) // turn on threshold counter
      );
      const touchstart = fromEvent(elementRef.nativeElement, 'touchstart').pipe(tap(event => {
        this.lastEvent = event;
      }), map(() => true));
      const touchEnd = fromEvent(elementRef.nativeElement, 'touchend').pipe(map(() => false));
      const mouseup = fromEvent(window, 'mouseup').pipe(filter(event => event.button == 0), map(() => false) // reset threshold counter
      );
      this.eventSubscribe = merge(mousedown, mouseup, touchstart, touchEnd).pipe(switchMap(state => state ? timer(this.threshold, 100) : of(null)), filter(() => this.lastEvent != null), takeUntil(this.onDestroy$)).subscribe(value => {
        // when treshold is met
        if (value) {
          this.lastEvent?.preventDefault();
          this.lastEvent?.stopPropagation();
          this.longClick.emit(this.lastEvent);
          this.lastEvent = null;
        }
        // on mouseup or touchend
        if (value === null) {
          this.click.emit(this.lastEvent);
          this.lastEvent = null;
        }
      });
    }
    unsubscribe() {
      if (this.eventSubscribe) {
        this.eventSubscribe.unsubscribe();
      }
    }
    static {
      this.ɵfac = function LongClickDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || LongClickDirective)(i0.ɵɵdirectiveInject(i0.ElementRef));
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: LongClickDirective,
        selectors: [["longClick"], ["", "longClick", ""], ["longClick"]],
        inputs: {
          threshold: [0, "longPressTreshold", "threshold"]
        },
        outputs: {
          longClick: "longClick",
          click: "click"
        },
        standalone: false,
        features: [i0.ɵɵNgOnChangesFeature]
      });
    }
  }
  return LongClickDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let ApplyFunctionPipe = /*#__PURE__*/(() => {
  class ApplyFunctionPipe {
    /**
     * @param callbackMethod method of arrow-function that is to be run using `ApplyFunctionPipe`.
     ** please pass an arrow function instead of regular class method
     **/
    transform(value, callbackMethod, ...extra) {
      return callbackMethod(value, ...extra);
    }
    static {
      this.ɵfac = function ApplyFunctionPipe_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || ApplyFunctionPipe)();
      };
    }
    static {
      this.ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
        name: "apply",
        type: ApplyFunctionPipe,
        pure: true
      });
    }
  }
  return ApplyFunctionPipe;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let AzavistaSharedModule = /*#__PURE__*/(() => {
  class AzavistaSharedModule {
    static {
      this.ɵfac = function AzavistaSharedModule_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || AzavistaSharedModule)();
      };
    }
    static {
      this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
        type: AzavistaSharedModule
      });
    }
    static {
      this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
        imports: [CommonModule, FormsModule, TranslateModule, MatSnackBarModule, CommonModule, FormsModule, TranslateModule]
      });
    }
  }
  return AzavistaSharedModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let AdvancedSearchLibService = /*#__PURE__*/(() => {
  class AdvancedSearchLibService {
    getSelectAndMultiSelectFields(fields) {
      return getSelectTypeFields(fields);
    }
    getFieldTypeFromField(field) {
      return fieldType(field);
    }
    convertFlatSearchToRecursive(flatSearchParams, fieldMode) {
      return flatSearchToRecursive(flatSearchParams, fieldMode);
    }
    convertRecursiveSearchToFlat(searchParams, fieldMode, fields) {
      return recursiveSearchToFlat(searchParams, fieldMode, fields);
    }
    static {
      this.ɵfac = function AdvancedSearchLibService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || AdvancedSearchLibService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: AdvancedSearchLibService,
        factory: AdvancedSearchLibService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return AdvancedSearchLibService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let TranslationService = /*#__PURE__*/(() => {
  class TranslationService {
    constructor(translateService) {
      this.translateService = translateService;
    }
    getTranslation(key) {
      return this.translateService.instant(key);
    }
    getCurrentLanguage() {
      return this.translateService.currentLang;
    }
    static {
      this.ɵfac = function TranslationService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || TranslationService)(i0.ɵɵinject(i1.TranslateService));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: TranslationService,
        factory: TranslationService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return TranslationService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let AzavistaSharedService = /*#__PURE__*/(() => {
  class AzavistaSharedService {
    constructor(advSearchLibSvc, translationService) {
      this.advSearchLibSvc = advSearchLibSvc;
      this.translationService = translationService;
      this.timeFormat = 'HH:mm';
    }
    executeOnNextLoop(actionBefore, action) {
      if (actionBefore) {
        actionBefore();
      }
      setTimeout(() => action());
    }
    getArrayItems(schema) {
      const items = schema.items;
      return items && items.enum ? items.enum : [];
    }
    getEnumItems(schema) {
      return schema.enum || [];
    }
    getArrayOrEnumItems(schema) {
      const arrayItems = this.getArrayItems(schema || {});
      const enumItems = this.getEnumItems(schema || {});
      const arrayOrEnumItems = arrayItems.length > 0 ? arrayItems : enumItems;
      return arrayOrEnumItems;
    }
    translate(key) {
      return this.translationService.getTranslation(key);
    }
    createObjectEnumFieldValue(items, selectedItemId, displayProperty = 'label') {
      const fieldvalue = {
        displayProperty: displayProperty,
        items: items,
        selected: items?.find(x => x.id === selectedItemId)
      };
      return fieldvalue;
    }
    createObjectEnumFieldValueDisplayingName(items, selectedItemId) {
      const fieldvalue = {
        displayProperty: 'name',
        items: items,
        selected: items?.find(x => x.id === selectedItemId)
      };
      return fieldvalue;
    }
    createObjectMultiSelectEnumFieldValueDisplayingLabel(items, selectedItems, keySelector) {
      const fieldvalue = {
        displayProperty: 'label',
        items: items,
        selected: items?.find(x => selectedItems.includes(keySelector(x)))
      };
      return fieldvalue;
    }
    operatorHasValue(operator) {
      if (operator === "empty" /* FieldOperator.empty */ || operator === "not_empty" /* FieldOperator.notEmpty */) {
        return false;
      }
      return true;
    }
    insertNullIfSchemaAllows(items, schemaType) {
      if (!items) {
        return;
      }
      const allowsNulls = this.schemaAllowsNulls(schemaType);
      if (!allowsNulls) {
        return;
      }
      this.instertItemOnFirstPlaceIfDoesNotExist(items, null);
    }
    instertItemOnFirstPlaceIfDoesNotExist(items, item) {
      const alreadyContains = items && items.indexOf(item) >= 0;
      if (alreadyContains) {
        return;
      }
      items.unshift(item);
    }
    getFieldOptions(fieldId, fieldsOptions) {
      if (!fieldsOptions) {
        return null;
      }
      const fieldIdWithOptions = fieldsOptions.find(x => x.fieldId === fieldId);
      if (!fieldIdWithOptions) {
        return null;
      }
      return fieldIdWithOptions.options;
    }
    schemaAllowsNulls(type) {
      if (Array.isArray(type) && type.indexOf('null') >= 0) {
        return true;
      }
      return false;
    }
    hasEmptyItem(array) {
      if (!array) {
        return false;
      }
      const hasEmptyItem = array.indexOf('') >= 0 || array.indexOf(null) >= 0 || array.indexOf(undefined) >= 0;
      return hasEmptyItem;
    }
    isEmptyStringOrNull(value) {
      return value === '' || value === null || value === undefined;
    }
    areSchemasSame(first, second) {
      // TODO: Rich schema comparison seems very expensive - this needs to be enriched with other schema variants
      const firstType = this.getFieldTypeFromField(first);
      const secondType = this.getFieldTypeFromField(second);
      if (firstType === secondType) {
        if (firstType === 'boolean') {
          // Booleans schema does not have anything else to compare
          return true;
        }
        if (first.schema.enum && !second.schema.enum || !first.schema.enum && second.schema.enum) {
          // One is of type enum but the other is not
          return false;
        }
        if (first.schema.enum && second.schema.enum) {
          // Enum type fields
          return this.areArraysSame(first.schema.enum, second.schema.enum);
        }
        if (firstType === 'multi-select') {
          const firstItems = first.schema.items;
          const secondItems = second.schema.items;
          // Array type fields
          if (firstItems.type !== secondItems.type) {
            // Items types are not equal
            return false;
          }
          return this.areArraysSame(firstItems.enum, secondItems.enum);
        }
        if (first.schema.format === second.schema.format) {
          // Same formats (like empty types or string with format of date, date-time, time, email etc).
          return true;
        }
      }
      return false;
    }
    areSchemaTypesSame(first, second) {
      if (Array.isArray(first) || Array.isArray(second)) {
        return this.areArraysSame(first, second);
      }
      return first === second;
    }
    areArraysSame(first, second, equalityComparer) {
      if (first && !second) {
        return false;
      }
      if (!first && second) {
        return false;
      }
      if (typeof first !== typeof second) {
        return false;
      }
      if (Array.isArray(first) && first.length !== second.length) {
        return false;
      }
      const secondCopy = [...second];
      const comparer = equalityComparer || ((left, right, index) => left === right);
      for (const item of first) {
        const foundAt = secondCopy.findIndex((x, index) => comparer(x, item, index));
        if (foundAt === -1) {
          return false;
        }
        secondCopy.splice(foundAt, 1);
      }
      return true;
    }
    setDateDisplayFormat(dateFormat) {
      this.dateFormat = dateFormat;
    }
    selectDateDisplayFormat(current) {
      return current || this.dateFormat;
    }
    setDateTimeDisplayFormat(dateTimeFormat) {
      this.dateTimeFormat = dateTimeFormat;
    }
    selectDateTimeDisplayFormat(current) {
      return current || this.dateTimeFormat;
    }
    setTimeDisplayFormat(timeDisplayFormat) {
      this.timeFormat = timeDisplayFormat;
    }
    selectTimeDisplayFormat(current) {
      return current || this.timeFormat;
    }
    setLocale(locale) {
      this.locale = locale;
    }
    selectLocale(current) {
      return current || this.locale;
    }
    convertEmptyAndNotEmptyOperators(criteria) {
      // When we send data to the server
      // FieldOperator.empty must become { empty: true }
      // FieldOperator.notEmpty must become { empty: false }
      const result = this.clone(criteria);
      for (const criteriaItem of result) {
        if (criteriaItem.operator === "empty" /* FieldOperator.empty */) {
          criteriaItem.value = true;
        } else if (criteriaItem.operator === "not_empty" /* FieldOperator.notEmpty */) {
          criteriaItem.operator = "empty" /* FieldOperator.empty */;
          criteriaItem.value = false;
        }
      }
      return result;
    }
    convertSeachParamEmptyFalseToNotEmptyOperator(criteria) {
      // When we read filters from the server
      // not_empty comes as {empty: false}
      // We need to change operator to FieldOperator.notEmpty if we receive { empty: false }
      for (const item of criteria) {
        if (item.operator === "empty" /* FieldOperator.empty */ && item.value === false) {
          // empty: false means FieldOperator.notEmpty
          item.operator = "not_empty" /* FieldOperator.notEmpty */;
        }
      }
    }
    criteriaToSearchParams(criteria, fieldMode) {
      const convertedCriteria = this.convertEmptyAndNotEmptyOperators(criteria);
      const advancedQuery = this.advSearchLibSvc.convertFlatSearchToRecursive(convertedCriteria, fieldMode);
      return advancedQuery;
    }
    searchParamsToCriteria(searchParams, allFields, fieldMode = 'name') {
      const criteria = this.advSearchLibSvc.convertRecursiveSearchToFlat(searchParams, fieldMode, allFields);
      // advanced-search library does not add all available operators for specified field and also the selected operator
      // We need to do it here
      for (const item of criteria) {
        item.allOperators = this.getAllOperatorsByField(item.field);
      }
      this.convertSeachParamEmptyFalseToNotEmptyOperator(criteria);
      this.setDefaultEmptyAndNotEmptyValues(criteria);
      return criteria;
    }
    setDefaultEmptyAndNotEmptyValues(criteria) {
      const containingField = criteria.filter(x => x.field);
      for (const item of containingField) {
        if (item.operator === "empty" /* FieldOperator.empty */ || item.operator === "not_empty" /* FieldOperator.notEmpty */) {
          item.value = this.getDefaultValueForField(item.field);
        }
      }
    }
    isNullOrWhitespace(value) {
      if (value === null || value === undefined || value === '') {
        return true;
      }
      if (typeof value === 'number') {
        // Numbers are never whitespace
        return false;
      }
      if (!(typeof value === 'string')) {
        // Non-strings are considered non whitespace
        return false;
      }
      const isEmptyString = !value.trim();
      return isEmptyString;
    }
    getAllOperatorsByField(field) {
      const operatorAvailabilities = this.getOperatorAvailabilitiesByFieldType();
      const allOperators = [];
      const allOperatorNames = this.getAllOperatorNames();
      for (const name of allOperatorNames) {
        const available = this.getOperatorAvailability(name, field, operatorAvailabilities);
        if (available) {
          allOperators.push({
            name: name,
            displayText: this.getOperatorDisplayText(name, field.schema)
          });
        }
      }
      return allOperators;
    }
    getOperatorDisplayText(operatorName, schema) {
      // TODO: Translation ?
      if (this.isFieldEnum(schema)) {
        if (operatorName === "in" /* FieldOperator.in */) {
          return this.translationService.getTranslation('IS_ONE_OF');
        } else if (operatorName === "not_in" /* FieldOperator.notIn */) {
          return this.translationService.getTranslation('IS_NOT_ONE_OF');
        }
      }
      if (operatorName === "eq" /* FieldOperator.equal */) {
        return this.translationService.getTranslation('IS_EQUAL_TO_CASE_SENSITIVE');
      } else if (operatorName === "gt" /* FieldOperator.greaterThan */) {
        return this.translationService.getTranslation('IS_GREATER_THAN');
      } else if (operatorName === "gte" /* FieldOperator.greaterThanOrEqual */) {
        return this.translationService.getTranslation('IS_GREATER_OR_EQUAL_THAN');
      } else if (operatorName === "in" /* FieldOperator.in */) {
        return this.translationService.getTranslation('CONTAINS');
      } else if (operatorName === "lt" /* FieldOperator.lessThan */) {
        return this.translationService.getTranslation('IS_LESS_THAN');
      } else if (operatorName === "lte" /* FieldOperator.lessThanOrEqual */) {
        return this.translationService.getTranslation('IS_LESS_OR_EQUAL_THAN');
      } else if (operatorName === "not_eq" /* FieldOperator.notEqual */) {
        return this.translationService.getTranslation('IS_DIFFERENT_FROM');
      } else if (operatorName === "not_in" /* FieldOperator.notIn */) {
        return this.translationService.getTranslation('DOES_NOT_CONTAIN');
      } else if (operatorName === "empty" /* FieldOperator.empty */) {
        return this.translationService.getTranslation('IS_EMPTY');
      } else if (operatorName === "not_empty" /* FieldOperator.notEmpty */) {
        return this.translationService.getTranslation('IS_NOT_EMPTY');
      } else if (operatorName === "after" /* FieldOperator.after */) {
        return this.translationService.getTranslation('AFTER');
      } else if (operatorName === "before" /* FieldOperator.before */) {
        return this.translationService.getTranslation('BEFORE');
      } else if (operatorName === "like" /* FieldOperator.like */) {
        return this.translationService.getTranslation('TEXT_CONTAINS');
      } else if (operatorName === "not_like" /* FieldOperator.notLike */) {
        return this.translationService.getTranslation('TEXT_DOES_NOT_CONTAIN');
      } else {
        return operatorName;
      }
    }
    getOperatorAvailability(operatorName, field, operatorAvailabilities) {
      // const fieldType = this.getFieldTypeFromSchema(field.schema, field.component);
      const fieldType = this.getFieldTypeFromField(field);
      const result = operatorAvailabilities.some(x => x.fieldType === fieldType && x.availableOperators.indexOf(operatorName) >= 0);
      return result;
    }
    getAllOperatorNames() {
      const allOperatorNames = ["eq" /* FieldOperator.equal */, "not_eq" /* FieldOperator.notEqual */, "in" /* FieldOperator.in */, "not_in" /* FieldOperator.notIn */, "like" /* FieldOperator.like */, "not_like" /* FieldOperator.notLike */, "lt" /* FieldOperator.lessThan */, "lte" /* FieldOperator.lessThanOrEqual */, "gt" /* FieldOperator.greaterThan */, "gte" /* FieldOperator.greaterThanOrEqual */, "empty" /* FieldOperator.empty */, "not_empty" /* FieldOperator.notEmpty */, "after" /* FieldOperator.after */, "before" /* FieldOperator.before */];
      return allOperatorNames;
    }
    getOperatorAvailabilitiesByFieldType() {
      const operatorAvailabilities = [{
        fieldType: 'text',
        availableOperators: ["eq" /* FieldOperator.equal */, "not_eq" /* FieldOperator.notEqual */, "like" /* FieldOperator.like */, "not_like" /* FieldOperator.notLike */, "empty" /* FieldOperator.empty */, "not_empty" /* FieldOperator.notEmpty */]
      }, {
        fieldType: 'virtual-textarea',
        availableOperators: ["eq" /* FieldOperator.equal */, "not_eq" /* FieldOperator.notEqual */, "like" /* FieldOperator.like */, "not_like" /* FieldOperator.notLike */, "empty" /* FieldOperator.empty */, "not_empty" /* FieldOperator.notEmpty */]
      }, {
        fieldType: 'url',
        availableOperators: ["eq" /* FieldOperator.equal */, "not_eq" /* FieldOperator.notEqual */, "like" /* FieldOperator.like */, "not_like" /* FieldOperator.notLike */, "empty" /* FieldOperator.empty */, "not_empty" /* FieldOperator.notEmpty */]
      }, {
        fieldType: 'email',
        availableOperators: ["eq" /* FieldOperator.equal */, "not_eq" /* FieldOperator.notEqual */, "like" /* FieldOperator.like */, "not_like" /* FieldOperator.notLike */, "empty" /* FieldOperator.empty */, "not_empty" /* FieldOperator.notEmpty */]
      }, {
        fieldType: 'integer',
        availableOperators: ["eq" /* FieldOperator.equal */, "not_eq" /* FieldOperator.notEqual */, "gt" /* FieldOperator.greaterThan */, "gte" /* FieldOperator.greaterThanOrEqual */, "lt" /* FieldOperator.lessThan */, "lte" /* FieldOperator.lessThanOrEqual */]
      }, {
        fieldType: 'opinion-scale',
        availableOperators: ["eq" /* FieldOperator.equal */, "not_eq" /* FieldOperator.notEqual */, "gt" /* FieldOperator.greaterThan */, "gte" /* FieldOperator.greaterThanOrEqual */, "lt" /* FieldOperator.lessThan */, "lte" /* FieldOperator.lessThanOrEqual */]
      }, {
        fieldType: 'rating',
        availableOperators: ["eq" /* FieldOperator.equal */, "not_eq" /* FieldOperator.notEqual */, "gt" /* FieldOperator.greaterThan */, "gte" /* FieldOperator.greaterThanOrEqual */, "lt" /* FieldOperator.lessThan */, "lte" /* FieldOperator.lessThanOrEqual */]
      }, {
        fieldType: 'number',
        availableOperators: ["eq" /* FieldOperator.equal */, "not_eq" /* FieldOperator.notEqual */, "gt" /* FieldOperator.greaterThan */, "gte" /* FieldOperator.greaterThanOrEqual */, "lt" /* FieldOperator.lessThan */, "lte" /* FieldOperator.lessThanOrEqual */]
      }, {
        fieldType: 'boolean',
        availableOperators: ["eq" /* FieldOperator.equal */]
      }, {
        fieldType: 'unknown',
        availableOperators: [
          // TODO: How we can provide possible operators for custom fields like objects ??
        ]
      }, {
        fieldType: 'date',
        availableOperators: ["eq" /* FieldOperator.equal */, "not_eq" /* FieldOperator.notEqual */, "after" /* FieldOperator.after */, "before" /* FieldOperator.before */, "empty" /* FieldOperator.empty */, "not_empty" /* FieldOperator.notEmpty */]
      }, {
        fieldType: 'datetime',
        availableOperators: ["eq" /* FieldOperator.equal */, "not_eq" /* FieldOperator.notEqual */, "after" /* FieldOperator.after */, "before" /* FieldOperator.before */, "empty" /* FieldOperator.empty */, "not_empty" /* FieldOperator.notEmpty */]
      }, {
        fieldType: 'travel-date',
        availableOperators: ["eq" /* FieldOperator.equal */, "not_eq" /* FieldOperator.notEqual */, "after" /* FieldOperator.after */, "before" /* FieldOperator.before */, "empty" /* FieldOperator.empty */, "not_empty" /* FieldOperator.notEmpty */]
      }, {
        fieldType: 'time',
        availableOperators: ["eq" /* FieldOperator.equal */, "not_eq" /* FieldOperator.notEqual */, "after" /* FieldOperator.after */, "before" /* FieldOperator.before */, "empty" /* FieldOperator.empty */, "not_empty" /* FieldOperator.notEmpty */]
      }, {
        fieldType: 'select',
        availableOperators: ["in" /* FieldOperator.in */, "not_in" /* FieldOperator.notIn */, "empty" /* FieldOperator.empty */, "not_empty" /* FieldOperator.notEmpty */]
      }, {
        fieldType: 'multi-select',
        availableOperators: ["eq" /* FieldOperator.equal */, "not_eq" /* FieldOperator.notEqual */, "in" /* FieldOperator.in */, "not_in" /* FieldOperator.notIn */, "empty" /* FieldOperator.empty */, "not_empty" /* FieldOperator.notEmpty */]
      }, {
        fieldType: 'image',
        availableOperators: ["empty" /* FieldOperator.empty */, "not_empty" /* FieldOperator.notEmpty */]
      }, {
        fieldType: 'document',
        availableOperators: ["empty" /* FieldOperator.empty */, "not_empty" /* FieldOperator.notEmpty */]
      }, {
        fieldType: 'virtual-autocomplete',
        availableOperators: ["eq" /* FieldOperator.equal */, "not_eq" /* FieldOperator.notEqual */, "empty" /* FieldOperator.empty */, "not_empty" /* FieldOperator.notEmpty */]
      }];
      return operatorAvailabilities;
    }
    getNonVisibleFieldsFromFilterCriteriaData(allFilters, filter, allFields) {
      const usedFields = this.getUsedFilterCriteriaFields(allFilters, filter);
      const filteredFields = this.getVisibleAndUsedFields(allFields, usedFields.map(x => x.id));
      return filteredFields;
    }
    getUsedFilterCriteriaFields(allFilters, filter) {
      const usedFields = [];
      if (allFilters) {
        for (const filter of allFilters) {
          if (filter.criteria) {
            for (const criteria of filter.criteria) {
              if (criteria.field) {
                usedFields.push(criteria.field);
              }
            }
          }
        }
      }
      if (filter && filter.criteria) {
        for (const criteria of filter.criteria) {
          if (criteria.field) {
            if (!usedFields.find(x => x.id === criteria.field.id)) {
              usedFields.push(criteria.field);
            }
          }
        }
      }
      return usedFields;
    }
    getVisibleAndUsedFields(fields, usedFieldIds) {
      const result = [];
      for (const field of fields) {
        const shouldSkip = field.visible_for_planners === false && !usedFieldIds?.find(x => field.id === x);
        if (!shouldSkip) {
          result.push(field);
        }
      }
      return result;
    }
    convertFieldsForAdvancedFilter(fields) {
      if (!fields) {
        return [];
      }
      const result = [];
      for (const item of fields) {
        const newItem = this.clone(item);
        // TODO: The server wrongly returns 'preference' type for 'preferred_language' field - it should be 'select'
        if (newItem.type === 'preference') {
          newItem.type = 'select';
        }
        if (newItem.schema?.format === 'email') {
          delete newItem.schema.format;
        }
        if (this.isFieldSchemaNullable(newItem.schema?.type) && this.isFieldDateOrDateTime(newItem.schema?.format)) {
          // This is nullabel date/date-time - make it not nullable because advanced filter
          // has other means of comparing date/date-time with null/not null - with "Is Empty" and "Is Not Empty"
          newItem.schema.type = 'string';
        }
        newItem.editable = true;
        result.push(newItem);
      }
      return result;
    }
    isFieldDateOrDateTime(schemaFormat) {
      return schemaFormat === 'date' || schemaFormat === 'date-time';
    }
    isFieldEnum(schema) {
      const fieldType = this.getFieldTypeFromField({
        schema: schema
      });
      return fieldType === 'select';
    }
    convertEnumToArray(schema) {
      if (this.isFieldEnum(schema)) {
        schema.type = 'array';
        schema.items = {
          type: 'string',
          enum: schema.enum
        };
        delete schema.enum;
      }
    }
    convertFieldEnumToArray(field, forceMultiSelectComponentType) {
      if (this.isFieldEnum(field.schema)) {
        this.convertEnumToArray(field.schema);
        if (field.schema.type === 'array') {
          if (forceMultiSelectComponentType) {
            field.component = forceMultiSelectComponentType;
          } else if (field.component === "radio_buttons" /* FieldComponentType.radioButtons */) {
            field.component = "default" /* FieldComponentType.default */;
          }
        }
      }
    }
    addIfDoesNotExist(value, array) {
      if (array.indexOf(value) >= 0) {
        // Already exists - not added
        return false;
      }
      array.push(value);
      return true;
    }
    removeIfExists(value, array) {
      const index = array.indexOf(value);
      if (index === -1) {
        // Does not exist - not removed
        return false;
      }
      array.splice(index, 1);
      return true;
    }
    generateUniqueString(prefix, existing) {
      let result = prefix;
      let suffix = 0;
      let isFree = false;
      do {
        const uppercasedResult = result.toUpperCase();
        const found = existing.find(x => x.toUpperCase() === uppercasedResult);
        if (!found) {
          isFree = true;
        } else {
          suffix++;
          result = prefix + '' + suffix;
        }
      } while (!isFree);
      return result;
    }
    isAccessAllowedByTeams(teamIds, requiredScopes) {
      // If no token exists - return true
      // If Required is undefined - true
      // If is admin in the token - true
      // If TeamIds is not defined - use user team scopes from the token
      // Perform search of at least one required scope in the team scopes and if found - return true
      // If no token exists - allow
      if (!this.aclData) {
        return true;
      }
      // If admin - allow
      if (this.aclData.isAdmin) {
        return true;
      }
      // Required scopes not provided - allow
      if (!requiredScopes) {
        return true;
      }
      // Team ids not provided - use these from token
      if (!teamIds) {
        teamIds = Object.keys(this.aclData.teamsWithScopes).map(teamId => +teamId);
      }
      for (const reqScope of requiredScopes) {
        for (const teamId of teamIds) {
          const teamScopes = this.aclData.teamsWithScopes[teamId];
          if (teamScopes) {
            const found = teamScopes.indexOf(reqScope) >= 0;
            if (found) {
              // At least one of required scopes is found in one of the teams - allow
              return true;
            }
          }
        }
      }
      return false;
    }
    hasAccess(acl) {
      if (!acl) {
        return true;
      }
      if (acl.isAdmin) {
        return true;
      }
      if (!acl.requiredScopes) {
        return true;
      }
      if (acl.requiredScopes.length === 0) {
        return false;
      }
      let availableScopes = acl.availableScopes;
      if (availableScopes && availableScopes.length === 0) {
        return false;
      }
      if (!availableScopes) {
        if (!this.aclData) {
          // Available scopes not provided and there is no token - allow
          return true;
        }
        // Use scopes from token
        availableScopes = [];
        Object.keys(this.aclData.teamsWithScopes).map(teamId => this.aclData.teamsWithScopes[teamId] || []).forEach(scopes => availableScopes.push(...scopes));
      }
      for (const requiredScope of acl.requiredScopes) {
        if (availableScopes.indexOf(requiredScope) >= 0) {
          return true;
        }
      }
      return false;
    }
    setAclData(aclData) {
      this.aclData = aclData;
      if (aclData) {
        const teamIds = Object.keys(aclData.teamsWithScopes).map(teamId => +teamId);
        this.aclDataScopes = [];
        for (const teamId of teamIds) {
          if (aclData.teamsWithScopes[teamId]) {
            this.aclDataScopes.push(...aclData.teamsWithScopes[teamId]);
          }
        }
      } else {
        this.aclDataScopes = null;
      }
    }
    clone(obj) {
      return clone(obj);
    }
    getDefaultValueForField(field) {
      const fieldType = this.getFieldTypeFromField(field);
      if (fieldType === 'multi-select') {
        return [];
      } else if (fieldType === 'text' || fieldType === 'email' || fieldType === 'select') {
        return '';
      } else if (fieldType === 'date' || fieldType === 'datetime' || fieldType === 'travel-date') {
        return '';
      } else if (fieldType === 'time') {
        // TODO: Do we need this as default value ?
        return '00:00';
      } else if (fieldType === 'boolean') {
        return false;
      } else if (fieldType === 'integer' || fieldType === 'number') {
        return 0;
      } else if (fieldType === 'unknown' && this.getNonNullSchemaType(field.schema?.type) === 'object') {
        // TODO: What to return if the field type is unknown fo the library but somehow known for the UI ?
        return {};
      } else if (fieldType === 'virtual-autocomplete') {
        return {
          displayProperty: 'name',
          items: [],
          selected: null,
          reportProperty: 'id'
        };
      } else {
        return '';
      }
    }
    createDefaultAutocompleteInputFieldValue() {
      return {
        displayProperty: 'name',
        items: [],
        selected: null,
        reportProperty: 'id'
      };
    }
    leftPadTime(value) {
      if (value < 10) {
        return '0' + value;
      }
      return '' + value;
    }
    getSelectAndMultiSelectFields(fields) {
      return this.advSearchLibSvc.getSelectAndMultiSelectFields(fields);
    }
    isFieldVirtual(field) {
      const fieldType = this.getFieldTypeFromField(field);
      return fieldType.indexOf('virtual-') === 0;
    }
    getFieldTypeFromField(field) {
      // Fields without component value or if it is default or timezone are not virtual
      // Timezone will be treated either as select or virtual-autocomplete inside the input field
      if (field.component && field.component !== "default" /* FieldComponentType.default */ && field.component !== "timezone" /* FieldComponentType.timezone */ && field.component !== "checkboxes" /* FieldComponentType.checkboxes */ && field.component !== "radio_buttons" /* FieldComponentType.radioButtons */) {
        return 'virtual-' + field.component;
      }
      let libFieldType = this.advSearchLibSvc.getFieldTypeFromField(field);
      // 'preference' is deprecated (used only for preferred_language field) and it behaves like 'select'
      if (libFieldType === 'preference') {
        libFieldType = 'select';
      }
      return libFieldType;
    }
    getNonNullSchemaType(schemaType) {
      if (typeof schemaType === 'string') {
        return schemaType;
      }
      if (Array.isArray(schemaType)) {
        const typeArr = [...schemaType];
        // Remove 'null' item from the array
        typeArr.splice(typeArr.indexOf('null'));
        // And return the first from the rest
        return typeArr[0];
      }
      return schemaType;
    }
    isFieldSchemaNullable(schemaType) {
      if (Array.isArray(schemaType)) {
        return schemaType.includes('null');
      }
      return false;
    }
    isCustomField(field) {
      return field.component && field.component !== 'default';
    }
    createCalculatedPagingOptions(po) {
      const result = {};
      result.length = po.object_count;
      if (po.pageSizes.indexOf(po.limit) === -1) {
        result.pageSizes = po.pageSizes.concat(po.limit).sort();
      } else {
        result.pageSizes = po.pageSizes;
      }
      result.pageIndex = Math.floor(po.offset / po.limit);
      result.pageSize = po.limit;
      return result;
    }
    areValuesEqual(first, second) {
      if (first === second) {
        return true;
      }
      if (first instanceof Date && second instanceof Date) {
        // Dates are object instances and they can not be compared with === or ==
        // Convert them to numbers before comparison
        return +first === +second;
      } else if (first instanceof Array && second instanceof Array) {
        if (first.length !== second.length) {
          return false;
        }
        if (first.length === 0 && second.length === 0) {
          return true;
        }
        const areAllItemEqual = first.every((val, index) => val === second[index]);
        return areAllItemEqual;
      } else {
        return first === second;
      }
    }
    getGroups(items, keySelector) {
      const map = {};
      for (const item of items) {
        const key = keySelector(item);
        if (map[key]) {
          map[key].push(item);
        } else {
          map[key] = [item];
        }
      }
      const result = Object.keys(map).map(x => ({
        key: x,
        items: map[x]
      }));
      return result;
    }
    getActionMenuItemDisabledState(menuAction, wholeSelection, selectedRowIdsAndTeams, dataSource) {
      const isDisabledBySingleSelection = this.getActionMenuItemDisabledStateBySelection(menuAction.singleSelectionOnly, wholeSelection, dataSource);
      if (isDisabledBySingleSelection) {
        return true;
      }
      const isDisabledByAclScopes = this.isActionMenuDisabledByAclScopes(menuAction, wholeSelection, selectedRowIdsAndTeams);
      if (isDisabledByAclScopes) {
        return true;
      }
      return false;
    }
    isActionMenuDisabledByAclScopes(menuAction, wholeSelection, selectedRowIdsAndTeams) {
      for (const selectedRowId of wholeSelection.selected) {
        const selectedRowSelectionData = selectedRowIdsAndTeams.find(x => x.id === selectedRowId);
        if (selectedRowSelectionData) {
          const isAllowed = this.isAccessAllowedByTeams(selectedRowSelectionData.teamIds, menuAction.requiredScopes);
          if (!isAllowed) {
            // Disabled by some of the selected rows
            return true;
          }
        }
      }
      return false;
    }
    getActionMenuItemDisabledStateBySelection(singleSelectionOnly, selection, dataSource) {
      if (singleSelectionOnly && selection.selected.length > 1) {
        return true;
      }
      if (singleSelectionOnly && this.getCurrentPageSelectedRowIds(dataSource, selection).length > 1) {
        return true;
      }
      return false;
    }
    getCurrentPageSelectedRowIds(dataSource, entireSelection) {
      if (!dataSource) {
        return [];
      }
      const currentPageRowIds = dataSource.map(x => x.id);
      const selectionIntersection = currentPageRowIds.filter(x => entireSelection.isSelected(x));
      return selectionIntersection;
    }
    sortAlphabetically(array, valueSelector) {
      return sortAlphabetically(array, valueSelector);
    }
    getCurrentLanguage() {
      return this.translationService.getCurrentLanguage();
    }
    static {
      this.ɵfac = function AzavistaSharedService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || AzavistaSharedService)(i0.ɵɵinject(AdvancedSearchLibService), i0.ɵɵinject(TranslationService));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: AzavistaSharedService,
        factory: AzavistaSharedService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return AzavistaSharedService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const sortAlphabetically = (array, valueSelector) => {
  if (array.length <= 1) {
    return array;
  }
  return array.sort((left, right) =>
  // Convert value selectors to strings to avoid throws of toLowerCase()
  (`${valueSelector(left)}` || '').toLowerCase().localeCompare((`${valueSelector(right)}` || '').toLowerCase()));
};
const clone = obj => {
  return JSON.parse(JSON.stringify(obj));
};
var CriteriaRelationOperator = /*#__PURE__*/function (CriteriaRelationOperator) {
  CriteriaRelationOperator["or"] = "or";
  CriteriaRelationOperator["and"] = "and";
  return CriteriaRelationOperator;
}(CriteriaRelationOperator || {});
const FIELD_OPERATOR_WITHOUT_VALUE = ["empty" /* FieldOperator.empty */, "not_empty" /* FieldOperator.notEmpty */];
const FIELD_OPERATOR_WITH_SINGLE_VALUE = ["after" /* FieldOperator.after */, "before" /* FieldOperator.before */, "eq" /* FieldOperator.equal */, "gt" /* FieldOperator.greaterThan */, "gte" /* FieldOperator.greaterThanOrEqual */, "lt" /* FieldOperator.lessThan */, "lte" /* FieldOperator.lessThanOrEqual */, "like" /* FieldOperator.like */, "not_eq" /* FieldOperator.notEqual */, "not_like" /* FieldOperator.notLike */];
const FIELD_OPERATOR_WITH_MULTIPLE_VALUE = ["in" /* FieldOperator.in */, "not_in" /* FieldOperator.notIn */];
let NotificationsService = /*#__PURE__*/(() => {
  class NotificationsService {
    constructor(snackBar) {
      this.snackBar = snackBar;
      this.duration = 8000;
      this.allMessages = [];
    }
    showMessage(type, message) {
      const config = {
        duration: this.duration,
        panelClass: ['azavista-components-notification-container', this.getTypeClass(type)]
      };
      const notificationMessage = {
        message: message,
        type: type,
        time: new Date().toISOString()
      };
      this.allMessages.push(notificationMessage);
      this.snackBar.open(message, null, config);
    }
    getAllMessages() {
      return this.allMessages;
    }
    clearMessages() {
      this.allMessages = [];
    }
    getTypeClass(type) {
      return `${type}-notification`;
    }
    static {
      this.ɵfac = function NotificationsService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || NotificationsService)(i0.ɵɵinject(i1$1.MatSnackBar));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: NotificationsService,
        factory: NotificationsService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return NotificationsService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
var CustomTableActionType = /*#__PURE__*/function (CustomTableActionType) {
  CustomTableActionType["button"] = "button";
  CustomTableActionType["textButton"] = "textButton";
  CustomTableActionType["dropDown"] = "drop-down";
  return CustomTableActionType;
}(CustomTableActionType || {});
var TimeType = /*#__PURE__*/function (TimeType) {
  TimeType["s"] = "Seconds";
  TimeType["m"] = "Minutes";
  TimeType["h"] = "Hours";
  TimeType["d"] = "Days";
  TimeType["w"] = "Weeks";
  return TimeType;
}(TimeType || {});
let UtilsService = /*#__PURE__*/(() => {
  class UtilsService {
    constructor() {}
    cloneDeep(obj) {
      const type = Object.prototype.toString.call(obj).slice(8, -1).toLowerCase();
      if (type === 'object') {
        return this.cloneObj(obj);
      }
      if (type === 'array') {
        return this.cloneArr(obj);
      }
      return obj;
    }
    cloneObj(obj) {
      const clone = {};
      for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
          clone[key] = this.cloneDeep(obj[key]);
        }
      }
      return clone;
    }
    cloneArr(arr) {
      return arr.map(item => this.cloneDeep(item));
    }
    static {
      this.ɵfac = function UtilsService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || UtilsService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: UtilsService,
        factory: UtilsService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return UtilsService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const snakeCase = text => {
  // all special chars are first replaced by ' ' then executes `trim()`
  // so we could prevent '_' become the prefix or suffix of the output
  const snakeCaseResult = text.trim().toLowerCase().replace(/[^a-z0-9]+/mg, ' ') // replace any consecutive special-chars with single ` `
  .trim().replace(/\s+/mg, '_');
  return snakeCaseResult;
};
function pick(data, propertyNames) {
  const objectWithUndefinedProperties = Object.entries(data).reduce((result, [key, value]) => {
    const keyWithType = key;
    if (propertyNames.includes(keyWithType)) {
      result[keyWithType] = value;
    }
    return result;
  }, {});
  const getPropertyAlongWithGetters = propertyNames.reduce((result, property) => {
    if (data[property] !== undefined) {
      result[property] = data[property];
    }
    return result;
  }, {});
  return {
    ...objectWithUndefinedProperties,
    ...getPropertyAlongWithGetters
  };
}
function omit(data, propsNames) {
  return pick(data, Object.keys(data).filter(key => !propsNames.includes(key)));
}
/** Safely parse JSON with try-catch */
const jsonParse = jsonString => {
  let result;
  try {
    result = JSON.parse(jsonString);
  } catch (e) {
    console.error(e);
    return undefined;
  }
  return result;
};
const getAzavistaMemoryCache = (proxyTarget, methodNames, methodNamesConfig) => {
  const cacheObj = {
    ___cacheCreateDate: new Date()
  };
  const cacheObjAny = cacheObj;
  const methods = {
    resetCache: () => {
      Object.keys(cacheObj).forEach(c => delete cacheObj[c]);
      cacheObj.___cacheCreateDate = new Date();
    },
    ___cacheObj: cacheObj
  };
  methodNames.forEach(name => {
    methods[name] = async (...params) => {
      const paramsStringified = JSON.stringify(params);
      if (cacheObjAny?.[name]?.[paramsStringified] !== undefined) {
        return firstValueFrom(cacheObjAny[name][paramsStringified]);
      }
      const replayObservable = scheduled(proxyTarget[name](...params), asapScheduler).pipe(shareReplay());
      if (cacheObjAny[name] == null) {
        cacheObjAny[name] = {};
      }
      cacheObjAny[name][paramsStringified] = replayObservable;
      if (methodNamesConfig?.[name]?.expireAfter) {
        setTimeout(() => {
          if (cacheObjAny[name]?.[paramsStringified]) {
            delete cacheObjAny[name]?.[paramsStringified];
          }
        }, methodNamesConfig[name].expireAfter);
      }
      return firstValueFrom(replayObservable);
    };
  });
  return methods;
};
const delay = ms => {
  return new Promise(resolve => {
    setTimeout(() => resolve(), ms);
  });
};
let AzavistaCacheService = /*#__PURE__*/(() => {
  class AzavistaCacheService {
    constructor() {
      this.storage = {};
    }
    setValue(key, value) {
      this.storage[key] = value;
    }
    getValue(key) {
      return this.storage[key];
    }
    removeValue(key) {
      delete this.storage[key];
    }
    removeAllValues() {
      this.storage = {};
    }
    static {
      this.ɵfac = function AzavistaCacheService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || AzavistaCacheService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: AzavistaCacheService,
        factory: AzavistaCacheService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return AzavistaCacheService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let AzavistaElementAttributesDirective = /*#__PURE__*/(() => {
  class AzavistaElementAttributesDirective {
    get attributes() {
      return this._attributes;
    }
    set attributes(value) {
      this.applyAttributesToElement(value);
      this._attributes = value;
    }
    constructor(host) {
      this.host = host;
    }
    applyAttributesToElement(value) {
      if (typeof value !== 'object') {
        return;
      }
      const diff = createPatch(this.attributes, value);
      diff.forEach(item => {
        switch (item.op) {
          case 'add':
          case 'replace':
          case 'move':
          case 'copy':
          case 'test':
            // other than remove will be handled by the following `Object.assign`
            break;
          case 'remove':
            this.host.nativeElement.removeAttribute(item.path.split('/').filter(path => !!path)[0]);
            break;
        }
      });
      Object.assign(this.host.nativeElement, value || {});
    }
    static {
      this.ɵfac = function AzavistaElementAttributesDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || AzavistaElementAttributesDirective)(i0.ɵɵdirectiveInject(i0.ElementRef));
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: AzavistaElementAttributesDirective,
        selectors: [["", "azavistaElementAttributes", ""]],
        inputs: {
          attributes: [0, "azavistaElementAttributes", "attributes"]
        }
      });
    }
  }
  return AzavistaElementAttributesDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Generated bundle index. Do not edit.
 */

export { AdvancedSearchLibService, ApplyFunctionPipe, AzavistaCacheService, AzavistaElementAttributesDirective, AzavistaSharedModule, AzavistaSharedService, CriteriaRelationOperator, CustomTableActionType, FIELD_OPERATOR_WITHOUT_VALUE, FIELD_OPERATOR_WITH_MULTIPLE_VALUE, FIELD_OPERATOR_WITH_SINGLE_VALUE, LongClickDirective, NotificationsService, TimeType, TranslationService, UtilsService, clone, delay, getAzavistaMemoryCache, jsonParse, omit, pick, snakeCase, sortAlphabetically };
